import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';

export type StudySubjectListItem =
  paths['/v1/clinical-study/studies/{studyId}/subjects']['get']['responses'][200]['content']['application/json'][number];

export type StudySubjectListState =
  | { status: 'loading' }
  | {
      status: 'ready';
      subjects: StudySubjectListItem[];
      /** A reload of the same study is in flight: the rows stay, they are not sent back to
       * `'loading'` (ui-design-guidelines § Refreshing is not loading). */
      refreshing?: boolean;
      /** Bumped by every completed read, so a reader can re-read what depends on the list. */
      version?: number;
    }
  | { status: 'error' };

/**
 * SL-0045 Activity 7: the single reader of `GET /v1/clinical-study/studies/{studyId}/subjects`,
 * shared by the two surfaces that used to demand a hand-typed Study Subject UUID —
 * `StudySubjectsSection` (the list itself) and `IdentifierCorrectionSection` (the administrative
 * correction form's subject field).
 *
 * OWNED BY `ClinicalStudyDetailPage`, which calls it ONCE and passes the result down to both
 * consumers — the same single-fetch shape `OrganizationDetailPage` uses for `GET
 * /v1/platform/users` across `UsersSection`/`RolesSection`. Calling it separately in each section
 * would issue two identical requests per page load and, worse, leave two independent copies of the
 * same list: a create in one section would refresh only its own.
 *
 * Deliberately NARROWER than `useTreatmentDatePoolPicker`: that hook carries a `'forbidden'` state
 * because its route is guarded by a study-administrator-only permission the operational roles do
 * not hold, so it degrades to a raw-ID input. This route's permission
 * (`clinical-study:study-subject:read`) is granted to ALL THREE clinical-study roles, so a 403 here
 * is not a routine role fact to design around — it is a genuine failure, and it lands in
 * `'error'` with a retry like any other. No raw-ID fallback is offered, which is also the direction
 * Activity 9 takes for the pool picker's own fallback.
 *
 * The server orders `createdAt DESC` and this hook never re-sorts.
 */
export function useStudySubjectList(studyId: string): StudySubjectListState & {
  reload: () => void;
} {
  const [state, setState] = useState<StudySubjectListState>({ status: 'loading' });
  // Staleness guard: `studyId` can change WITHOUT a remount (the detail route keeps the same
  // component and only the param changes), so an earlier study's response can land after a later
  // one's. Only the newest request is allowed to write state; older ones resolve and are dropped.
  const latestRequest = useRef(0);
  // The study whose rows the `'ready'` state holds: a reload of that study refreshes in place, a
  // different study starts from `'loading'`.
  const readyFor = useRef<string | null>(null);

  const load = useCallback(() => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setState((previous) =>
      previous.status === 'ready' && readyFor.current === studyId
        ? { ...previous, refreshing: true }
        : { status: 'loading' },
    );
    // A failed refresh keeps the rows it was refreshing (stale but readable); a failed first read
    // is the `'error'` state with its retry.
    const failed = (previous: StudySubjectListState): StudySubjectListState =>
      previous.status === 'ready' && readyFor.current === studyId
        ? { ...previous, refreshing: false }
        : { status: 'error' };
    apiClient
      .GET('/v1/clinical-study/studies/{studyId}/subjects', {
        params: { path: { studyId } },
      })
      .then(({ data, error, response }) => {
        if (latestRequest.current !== requestId) {
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          setState(failed);
          return;
        }
        readyFor.current = studyId;
        setState((previous) => ({
          status: 'ready',
          subjects: data,
          refreshing: false,
          version: (previous.status === 'ready' ? (previous.version ?? 0) : 0) + 1,
        }));
      })
      .catch(() => {
        if (latestRequest.current === requestId) {
          setState(failed);
        }
      });
  }, [studyId]);

  useEffect(load, [load]);

  return { ...state, reload: load };
}

/** What `ClinicalStudyDetailPage` passes to each consuming section. */
export type StudySubjectListSource = StudySubjectListState & { reload: () => void };
